// Glue between the lesson screen and the measurement engine (spec §1-10/§1-11,
// extended to video + assessment in Phase 2 task 2-3c). Measurement is
// enabled for TEXT, VIDEO and EXAM (assessment) lessons, only when the school
// flag is on. Runs alongside (does not replace) the legacy trackingLesson —
// this is the new side of the dual write.

import { ActivityMeter, bindActivityMeterToDom, LessonType } from './activityMeter'
import {
  SessionReporter,
  bindSessionReporterToDom,
  CompletionState
} from './sessionReporter'
import { sendLearningEvent } from './eventsReporter'

export interface MeterSession {
  start(): void
  stop(): Promise<void>
  setProgress(progress: number): void
  setCompletion(state: CompletionState): void
  setMediaPlaying(playing: boolean): void
  recordInteraction(): void
  emitEvent(eventType: string, payload?: Record<string, unknown>): void
  dispose(): void
}

export interface SyncOptions {
  enabled: boolean
  lessonId: number
  lessonType: string
  diagRate: number
}

export type MeterSessionFactory = (lessonId: number, lessonType: LessonType, diagRate: number) => MeterSession

// Raw server lessonType -> meter LessonType (mirrors Learning::LessonAuthorization::LESSON_TYPE_MAP).
const LESSON_TYPE_MAP: Record<string, LessonType> = {
  text: 'text',
  video: 'video',
  live: 'video',
  exam: 'assessment'
}
const MEASURABLE_TYPES = new Set(['text', 'video', 'live', 'exam'])

export function defaultMeterSessionFactory(lessonId: number, lessonType: LessonType, diagRate: number): MeterSession {
  const meter = new ActivityMeter(lessonType)
  const reporter = new SessionReporter({ lessonId, meter, diagRate })
  let detachMeter: (() => void) | null = null
  let detachReporter: (() => void) | null = null

  return {
    start() {
      reporter.start()
      if (typeof document !== 'undefined') {
        meter.setVisible(document.visibilityState === 'visible')
        meter.setFocused(document.hasFocus())
      }
      detachMeter = bindActivityMeterToDom(meter)
      detachReporter = bindSessionReporterToDom(reporter)
    },
    async stop() {
      await reporter.stop()
    },
    setProgress(progress: number) {
      meter.setProgress(progress)
    },
    setCompletion(state: CompletionState) {
      void reporter.setCompletionState(state)
    },
    setMediaPlaying(playing: boolean) {
      meter.setMediaPlaying(playing)
    },
    recordInteraction() {
      meter.recordInteraction()
    },
    emitEvent(eventType: string, payload?: Record<string, unknown>) {
      void sendLearningEvent({ lessonId, sessionUuid: reporter.currentSessionUuid, eventType, payload })
    },
    dispose() {
      detachMeter?.()
      detachReporter?.()
      detachMeter = null
      detachReporter = null
    }
  }
}

export class LearningMeterController {
  private session: MeterSession | null = null
  private currentLessonId = 0
  private readonly factory: MeterSessionFactory

  constructor(opts: { factory?: MeterSessionFactory } = {}) {
    this.factory = opts.factory ?? defaultMeterSessionFactory
  }

  // Start/stop the meter to match the current lesson and flag state. Idempotent.
  // Returns true iff this call started a new session (used to detect "opened
  // an already-completed lesson again" -> lesson_reopened, spec task 2-3b).
  sync({ enabled, lessonId, lessonType, diagRate }: SyncOptions): boolean {
    const measurable = enabled && MEASURABLE_TYPES.has(lessonType) && lessonId > 0
    if (!measurable) {
      this.finalize()
      return false
    }
    if (this.currentLessonId === lessonId && this.session) return false

    this.finalize()
    this.session = this.factory(lessonId, LESSON_TYPE_MAP[lessonType] ?? 'text', diagRate)
    this.session.start()
    this.currentLessonId = lessonId
    return true
  }

  setCompletion(state: CompletionState): void {
    this.session?.setCompletion(state)
  }

  setProgress(progress: number): void {
    this.session?.setProgress(progress)
  }

  setMediaPlaying(playing: boolean): void {
    this.session?.setMediaPlaying(playing)
  }

  // video_progressed: dual-emitted 25/50/75/90% playback signal (task 2-3a).
  emitVideoMilestone(milestone: number): void {
    this.session?.emitEvent('video_progressed', { milestone })
  }

  // Bridged from the exam iframe on answer save (task 2-3, "テスト回答シグナル").
  // Also counts as an interaction so the assessment meter's confirmed-time
  // window (spec §2.2: recent answer/select/input/question-move) advances —
  // without this, an exam session would only ever accrue estimated time,
  // since no DOM activity inside the iframe reaches this page's listeners.
  emitAssessmentAnswered(): void {
    this.session?.recordInteraction()
    this.session?.emitEvent('assessment_answered')
  }

  // lesson_completed / lesson_reopened: dual-emitted alongside the legacy
  // completion API call (task 2-3b).
  emitLessonCompleted(): void {
    this.session?.emitEvent('lesson_completed')
  }

  emitLessonReopened(): void {
    this.session?.emitEvent('lesson_reopened')
  }

  dispose(): void {
    this.finalize()
  }

  private finalize(): void {
    const session = this.session
    if (session) {
      void session.stop() // final flush; counters are cumulative so this is best-effort
      session.dispose()
    }
    this.session = null
    this.currentLessonId = 0
  }
}
